import { useCallback, useContext, useState } from 'react';

import { AuthContext } from 'auth';

import { readFileAsDataUrl } from './fileReader';
import { IReturnValue } from './interfaces';

export const useUploadFile = (
  onUpload?: (fileLink: string) => void,
  options?: { multiple?: boolean; local?: boolean }
): IReturnValue => {
  const {
    data: { tokens },
  } = useContext(AuthContext);

  const [isUploading, setIsUploading] = useState(false);
  const { access_token } = tokens ?? {};
  const isLocal = options?.local ?? false;

  const uploadSingle = useCallback(
    async (file: File): Promise<string> => {
      if (isLocal) return readFileAsDataUrl(file);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/upload`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || 'Upload failed');
      }

      return data.path;
    },
    [access_token, isLocal]
  );

  const uploadImage = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        const url = await uploadSingle(file);
        onUpload?.(url);
        return url;
      } finally {
        setIsUploading(false);
      }
    },
    [uploadSingle, onUpload]
  );

  const uploadImages = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      try {
        const results = await Promise.all(files.map(uploadSingle));
        return results;
      } finally {
        setIsUploading(false);
      }
    },
    [uploadSingle]
  );

  return {
    uploadImage,
    uploadImages: options?.multiple ? uploadImages : undefined,
    isUploading,
  };
};
